/* focus-trap.js — minimal shared focus-trap utility.
 * Used by mobile-nav, age-gate, and any modal that needs to restrict Tab/Shift-Tab
 * traversal to elements inside a single root. Vanilla JS, no deps.
 *
 * API:
 *   const trap = KoshoFocusTrap.create(rootEl);
 *   trap.activate();   // begins listening for Tab/Shift-Tab; focuses first element
 *   trap.deactivate(); // stops listening; restores focus to previously-focused element
 */

(function (global) {
  'use strict';

  var FOCUSABLE_SELECTOR = [
    'a[href]',
    'area[href]',
    'button:not([disabled])',
    'input:not([disabled]):not([type="hidden"])',
    'select:not([disabled])',
    'textarea:not([disabled])',
    '[tabindex]:not([tabindex="-1"])',
    'audio[controls]',
    'video[controls]',
    '[contenteditable]:not([contenteditable="false"])'
  ].join(',');

  function getFocusable(root) {
    if (!root) return [];
    var nodes = root.querySelectorAll(FOCUSABLE_SELECTOR);
    var out = [];
    for (var i = 0; i < nodes.length; i++) {
      var n = nodes[i];
      // Skip hidden / disabled / aria-hidden
      if (n.hasAttribute('disabled')) continue;
      if (n.getAttribute('aria-hidden') === 'true') continue;
      // offsetParent === null detects display:none in most cases
      if (n.offsetParent === null && n.tagName !== 'AREA') continue;
      out.push(n);
    }
    return out;
  }

  function create(root) {
    var previouslyFocused = null;
    var keydownHandler = null;
    var active = false;

    function onKeyDown(e) {
      if (e.key !== 'Tab') return;
      var focusable = getFocusable(root);
      if (focusable.length === 0) {
        e.preventDefault();
        return;
      }
      var first = focusable[0];
      var last = focusable[focusable.length - 1];
      var current = document.activeElement;
      if (e.shiftKey) {
        if (current === first || !root.contains(current)) {
          e.preventDefault();
          last.focus();
        }
      } else {
        if (current === last || !root.contains(current)) {
          e.preventDefault();
          first.focus();
        }
      }
    }

    function activate(options) {
      if (active) return;
      active = true;
      previouslyFocused = document.activeElement;
      keydownHandler = onKeyDown;
      document.addEventListener('keydown', keydownHandler, true);
      // Focus the first focusable inside, or root itself if none.
      var focusable = getFocusable(root);
      var target =
        (options && options.initialFocus) ||
        focusable[0] ||
        root;
      // Defer a tick so any open transition starts before focusing.
      setTimeout(function () {
        try { target.focus(); } catch (err) { /* ignore */ }
      }, 0);
    }

    function deactivate() {
      if (!active) return;
      active = false;
      if (keydownHandler) {
        document.removeEventListener('keydown', keydownHandler, true);
        keydownHandler = null;
      }
      if (previouslyFocused && typeof previouslyFocused.focus === 'function') {
        try { previouslyFocused.focus(); } catch (err) { /* ignore */ }
      }
      previouslyFocused = null;
    }

    return {
      activate: activate,
      deactivate: deactivate,
      isActive: function () { return active; }
    };
  }

  global.KoshoFocusTrap = {
    create: create,
    getFocusable: getFocusable
  };
})(window);
